import booleanContains from '@turf/boolean-contains';
import circle from '@turf/circle';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { v4 as uuidv4 } from 'uuid';

import { GEOMETRY_TYPE } from '../constants/map';

import { DEFAULT_LINE_COLOR, ICON } from '../components/Maps/constants';

const provider = new OpenStreetMapProvider();

export function reverseCoordinates(coordinates) {
  return Array.isArray(coordinates) && coordinates.every(item => Array.isArray(item))
    ? coordinates.map(c => [c?.at(1), c?.at(0)])
    : coordinates;
}

function normalizeShape(feature) {
  if (feature?.geometry?.type === GEOMETRY_TYPE.POINT && feature?.properties?.radius) {
    const center = feature.geometry.coordinates;
    const radiusInKm = feature.properties.radius / 1000;

    return circle(center, radiusInKm, { steps: 64 });
  }

  return feature;
}

export function isShapeInsideBoundary(boundary, geoJsonOrArray) {
  if (!boundary || !geoJsonOrArray) return false;

  const boundaryShape = normalizeShape(boundary);
  const geoJsonArray = Array.isArray(geoJsonOrArray) ? geoJsonOrArray : [geoJsonOrArray];

  try {
    for (const feature of geoJsonArray) {
      const shapeToTest = normalizeShape(feature);

      if (!booleanContains(boundaryShape, shapeToTest)) {
        return false;
      }
    }
  } catch (err) {
    return false;
  }

  return true;
}

export function addStartEndIcons(tracking) {
  if (!tracking?.features?.length) return tracking;

  const points = tracking.features.filter(x => x.geometry.type === GEOMETRY_TYPE.POINT);
  const startPoint = points?.at(0);
  const endPoint = points?.at(-1);

  return tracking?.features?.map(feature => {
    return feature.geometry?.type === GEOMETRY_TYPE.POINT
      ? feature === startPoint
        ? { ...feature, properties: { ...feature.properties, customIcon: ICON.START } }
        : feature === endPoint
          ? { ...feature, properties: { ...feature.properties, customIcon: ICON.END } }
          : feature
      : { ...feature, color: DEFAULT_LINE_COLOR };
  });
}

export function ConditionalWrapper({ condition, wrapper, children }) {
  return condition ? wrapper(children) : children;
}

export function buildPointFeature(data) {
  if (!data) return;

  return {
    type: 'Feature',
    geometry: {
      type: GEOMETRY_TYPE.POINT,
      coordinates: [data.tag?.lat, data.tag?.lng]
    },
    properties: {
      animal_id: data.id,
      customIcon: ICON.START,
      diagri_id: data.tag.diagri_id,
      eartag_management_id: data.eartag_management_id,
      eartag_official_id: data.eartag_official_id,
      farm_id: data.farm_id,
      hasAlerts: data.hasAlerts,
      identifier: data.identifier,
      isVisible: true,
      notifications: [],
      sex: data.sex,
      specie: data.specie.name,
      time: '',
      uniqueId: uuidv4()
    }
  };
}

function isValidPosition(position) {
  return (
    Array.isArray(position) &&
    position.length === 2 &&
    position.every(coordinate => typeof coordinate === 'number' && !Number.isNaN(coordinate))
  );
}

export async function getCoordinatesByAddress(address) {
  if (!address) return;

  try {
    const addressResults = await provider.search({ query: address });

    if (!Array.isArray(addressResults) || !addressResults.length) return;

    const { y: lat, x: lng } = addressResults[0] || {};
    const newPosition = [Number(lat), Number(lng)];

    return isValidPosition(newPosition) ? newPosition : null;
  } catch (error) {
    console.error('Error searching position by address:', error);
  }
}
